import { isDefaultCloudInstance } from "@app/helpers/utils/cloud-for-orgs"
import {
  AiChatAvailabilityDocument,
  AiChatAvailabilityQuery,
  AiChatAvailabilityQueryVariables,
} from "@app/helpers/backend/graphql"
import { runGQLQuery } from "@hoppscotch/common/helpers/backend/GQLClient"
import { AuthPlatformDef } from "@hoppscotch/common/platform/auth"
import {
  AIChatSelection,
  ExperimentsPlatformDef,
} from "@hoppscotch/common/platform/experiments"
import * as E from "fp-ts/Either"
import { z } from "zod"

/**
 * AI assistant transport, shared by the web and desktop experiments defs.
 *
 * Org instances only: the backend gates it per org (enterprise tier, admin
 * switch), and hoppscotch.io never mounts the assistant at all.
 */

type AIExperiments = NonNullable<ExperimentsPlatformDef["aiExperiments"]>
type ChatFn = NonNullable<AIExperiments["chat"]>
type AvailabilityFn = NonNullable<AIExperiments["getChatAvailability"]>

export type AIChatDeps = {
  auth: Pick<
    AuthPlatformDef,
    "getBackendHeaders" | "getCurrentUser" | "waitOrganizationInfoReady"
  >
  /** The signed-in Firebase user, to force a fresh ID token on a 401. */
  getFirebaseUser: () => {
    getIdToken: (forceRefresh?: boolean) => Promise<string>
  } | null
}

// Null (some gateways' "unknown") reads as absent.
const usageCounter = z
  .number()
  .nullish()
  .transform((value) => value ?? undefined)

const ChatResponseSchema = z.object({
  content: z.string(),
  tool_calls: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        input: z.record(z.any()),
      })
    )
    .default([]),
  // Only used for feedback; a gateway that omits it must not void the turn.
  trace_id: z.string().catch(""),
  // Counters are individually optional: only Anthropic reports all four.
  usage: z
    .object({
      input_tokens: usageCounter,
      cache_read_input_tokens: usageCounter,
      cache_creation_input_tokens: usageCounter,
      output_tokens: usageCounter,
    })
    .nullish()
    .transform((usage) => usage ?? undefined),
  model: z.string().optional(),
  assistant_content: z.array(z.any()).optional(),
  loaded_tools: z.array(z.string()).optional(),
})

/**
 * Backend `ai_experiments/*` mnemonics mapped onto the codes the shared chat
 * UI understands (see `AIChatService.describeChatError`).
 */
const BACKEND_ERROR_CODES = new Map<string, string>([
  ["ai_experiments/chat_disabled", "CHAT_DISABLED"],
  ["ai_experiments/chat_input_too_large", "INPUT_TOO_LARGE"],
  ["ai_experiments/invalid_chat_input", "INVALID_INPUT"],
  ["ai_experiments/cannot_run_chat", "CANNOT_RUN_CHAT"],
  ["ai_experiments/model_unavailable", "MODEL_UNAVAILABLE"],
  ["ai_experiments/provider_misconfigured", "PROVIDER_MISCONFIGURED"],
  ["ai_experiments/provider_rate_limited", "RATE_LIMITED"],
  ["ai_experiments/provider_timeout", "PROVIDER_TIMEOUT"],
  ["ai_experiments/rate_limited", "RATE_LIMITED"],
])

/** A backend body: Cloud's filter sends `{ message }` with no `statusCode`. */
const isBackendError = (body: unknown): body is { message: unknown } =>
  !!body &&
  typeof body === "object" &&
  !Array.isArray(body) &&
  "message" in body

/** `message` is a code, a list (validation), or a wrapped Nest error body. */
const backendMessages = (message: unknown, nested = false): string[] => {
  if (typeof message === "string") return [message]
  if (Array.isArray(message))
    return message.filter((m): m is string => typeof m === "string")
  if (!nested && isBackendError(message))
    return backendMessages(message.message, true)
  return []
}

const errorCodeFor = (status: number, body: unknown): string => {
  if (isBackendError(body)) {
    for (const m of backendMessages(body.message)) {
      const code = BACKEND_ERROR_CODES.get(m)
      if (code) return code
    }
  }
  // These mean the same thing whoever answered.
  if (status === 413) return "INPUT_TOO_LARGE"
  if (status === 429) return "RATE_LIMITED"
  if (status === 504) return "PROVIDER_TIMEOUT"
  // Anything else without a JSON body is a proxy or WAF page, not the backend.
  if (!isBackendError(body)) return "NETWORK"
  if (status === 401 || status === 403) return "UNAUTHORIZED"
  if (status === 400) return "INVALID_INPUT"
  return "CANNOT_RUN_CHAT"
}

/** A rejected ID token; 403 (not an org member) won't heal on refresh. */
const isAuthFailure = (status: number, body: unknown) =>
  status === 401 && isBackendError(body)

const disabled = () => ({ enabled: false, models: [], skills: [] })

export const createAIChat = ({ auth, getFirebaseUser }: AIChatDeps) => {
  // Null when signed out or the refresh fails; Firebase owns sign-out.
  const freshIdToken = async () => {
    try {
      return (await getFirebaseUser()?.getIdToken(true)) ?? null
    } catch (_e) {
      return null
    }
  }

  /** Whether the org's assistant is on, and which models and skills it offers. */
  const getChatAvailability: AvailabilityFn = async () => {
    // Guests can't chat, and the query needs a session anyway.
    if (!auth.getCurrentUser()) return E.right(disabled())

    const result = await runGQLQuery<
      AiChatAvailabilityQuery,
      AiChatAvailabilityQueryVariables,
      string
    >({ query: AiChatAvailabilityDocument, variables: {} })

    if (E.isLeft(result)) {
      if (result.left.type === "network_error") return E.left("NETWORK")
      // A backend without the assistant: off, not a retryable error.
      if (result.left.error.includes("Cannot query field"))
        return E.right(disabled())
      return E.left("CANNOT_READ_AI_CONFIG")
    }

    return E.right({
      enabled: result.right.aiChatEnabled,
      models: result.right.aiChatModelOptions,
      skills: result.right.aiChatSkills,
    })
  }

  const chat: ChatFn = async (
    messages,
    context,
    selection?: AIChatSelection,
    options?: { signal?: AbortSignal }
  ) => {
    const signal = options?.signal

    try {
      // A raw fetch skips the GQL client's org gate; x-organization-id needs it.
      await auth.waitOrganizationInfoReady?.()

      // Both fields or neither: a model without its connection is resolved
      // against the org default, which refuses models it does not offer.
      const body = JSON.stringify({
        messages,
        context,
        ...(selection
          ? { connectionID: selection.connectionID, model: selection.model }
          : {}),
      })

      const send = async (idToken?: string) => {
        const res = await fetch(
          `${import.meta.env.VITE_BACKEND_API_URL}/ai-experiments/chat`,
          {
            method: "POST",
            body,
            headers: {
              ...auth.getBackendHeaders(),
              ...(idToken ? { authorization: `Bearer ${idToken}` } : {}),
              "Content-Type": "application/json",
            },
            signal,
          }
        )
        // A proxy in front of the backend may answer with HTML; never throw.
        const data: unknown = await res.json().catch(() => null)
        return { res, data }
      }

      let reply = await send()

      // A stale ID token is refreshed and the turn resent once. The guard
      // rejected it before any provider call, so nothing ran twice.
      if (isAuthFailure(reply.res.status, reply.data) && !signal?.aborted) {
        const idToken = await freshIdToken()
        if (idToken && !signal?.aborted) reply = await send(idToken)
      }

      const { res, data } = reply

      if (signal?.aborted) return E.left("ABORTED")

      if (!res.ok) return E.left(errorCodeFor(res.status, data))

      const result = ChatResponseSchema.safeParse(data)

      if (!result.success) return E.left("UNABLE_TO_PARSE_RESPONSE")

      return E.right({
        content: result.data.content,
        tool_calls: result.data.tool_calls,
        trace_id: result.data.trace_id,
        model: result.data.model,
        usage: result.data.usage,
        assistant_content: result.data.assistant_content,
        loaded_tools: result.data.loaded_tools,
      })
    } catch (_e) {
      return E.left(signal?.aborted ? "ABORTED" : "NETWORK")
    }
  }

  return { chat, getChatAvailability }
}

/** The chat capabilities, or none on hoppscotch.io. */
export const aiChatExperiments = (
  deps: AIChatDeps
): Pick<AIExperiments, "chat" | "getChatAvailability"> =>
  isDefaultCloudInstance ? {} : createAIChat(deps)
